import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Button } from '@VicOne-RD/miraidon-ui/button';
import { DialogShell } from './dialog-shell';

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel: string;
  cancelLabel: string;
  danger?: boolean;
  /** Label shown on the confirm button while `action` runs. */
  busyLabel?: string;
  /**
   * Optional async work to run on confirm. When provided the dialog stays
   * open and disables both buttons (plus Escape/overlay-close) while the
   * promise is in flight; on success it resolves `true` and closes, on throw
   * it closes and re-throws so the caller's `catch` can surface the error.
   */
  action?: () => Promise<void>;
}

export type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface PendingState extends ConfirmOptions {
  resolve: (v: boolean) => void;
  reject: (e: unknown) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingState | null>(null);
  const [busy, setBusy] = useState(false);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve, reject) => {
      setPending({ ...opts, resolve, reject });
    });
  }, []);

  function settleCancel() {
    setPending((p) => {
      if (p && !busy) p.resolve(false);
      return busy ? p : null;
    });
  }

  async function onConfirm() {
    if (!pending || busy) return;
    if (!pending.action) {
      pending.resolve(true);
      setPending(null);
      return;
    }
    setBusy(true);
    try {
      await pending.action();
      pending.resolve(true);
      setBusy(false);
      setPending(null);
    } catch (e) {
      pending.reject(e);
      setBusy(false);
      setPending(null);
    }
  }

  const confirmLabel = pending
    ? busy && pending.busyLabel
      ? pending.busyLabel
      : pending.confirmLabel
    : '';

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <DialogShell
        open={pending != null}
        // While busy, Escape/overlay-click must be inert. DialogShell calls
        // onCancel for both; no-op when busy, otherwise resolve false + close.
        onCancel={() => {
          if (busy) return;
          settleCancel();
        }}
        title={pending?.title}
        description={pending?.message}
        width="narrow"
        testId="confirm-dialog"
      >
        <DialogShell.Footer>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              if (!busy) settleCancel();
            }}
            disabled={busy}
          >
            {pending?.cancelLabel}
          </Button>
          <Button
            type="button"
            variant={pending?.danger ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={busy}
            data-testid="confirm-dialog-submit"
          >
            {confirmLabel}
          </Button>
        </DialogShell.Footer>
      </DialogShell>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmProvider>');
  return ctx;
}

/** Pre-binds `danger: true`; forwards `action`/`busyLabel` unchanged. */
export function useDestructiveConfirm() {
  const confirm = useConfirm();
  return useMemo(
    () => (opts: Omit<ConfirmOptions, 'danger'>) => confirm({ ...opts, danger: true }),
    [confirm],
  );
}

